import React, { useState, useEffect, useRef, useCallback } from 'react';
import { cn } from "@/lib/utils";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { preprocessMarkdown } from '@/utils/markdownPreprocess';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ExternalLink, Loader2 } from 'lucide-react';
import InterviewMedia from '../InterviewMedia';
import { ArticleSources } from '../InterviewExpanded/ArticleSources';
import { Interview } from '@/types/interview';
import { AttentionZone } from '@/components/attention/AttentionZone';
import { useAttention } from '@/components/attention/AttentionContext';

interface ContentLayoutProps {
  isReversed: boolean;
  context: string;
  contextUrl?: string;
  contextImageUrl?: string;
  contextImageCaption?: string;
  question: string;
  answer: string;
  intervieweeName: string;
  title: string;
  jobTitle?: string;
  company: string;
  tags: string[];
  isExpanded: boolean;
  photoUrl?: string;
  photoUrl2?: string; // Second interviewee profile picture
  sponsorLogoUrl?: string; // Sponsor logo
  sponsorLogoPosition?: 'profile' | 'article' | 'both'; // Where to display the logo
  interviewId?: string;
  sourceArticles?: Interview['sourceArticles'];
  photographerCredit?: string;
  articleType?: Interview['articleType'];
  isSponsored?: boolean;
}

const ContentLayout = ({
  isReversed,
  context,
  contextUrl,
  contextImageUrl,
  contextImageCaption,
  question,
  answer,
  intervieweeName,
  title,
  jobTitle,
  company,
  tags,
  isExpanded,
  photoUrl,
  photoUrl2,
  sponsorLogoUrl,
  sponsorLogoPosition = 'profile',
  interviewId,
  sourceArticles,
  photographerCredit,
  articleType,
  isSponsored
}: ContentLayoutProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const attention = useAttention();
  
  // Check if this is a Briefr Bullets article (should hide NTB header)
  const isBriefBulletsArticle = articleType && (
    articleType === 'brief_bullets' ||
    articleType === 'brief_bullets_natt' ||
    articleType === 'brief_bullets_dag' ||
    articleType === 'brief_bullets_europa' ||
    articleType === 'ai_generated'
  );
  
  // State for smart iframe loading with fallback
  const [iframeStatus, setIframeStatus] = useState<'loading' | 'success' | 'failed'>('loading');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Reset iframe status when dialog opens
  const handleDialogOpen = useCallback((open: boolean) => {
    setIsDialogOpen(open);
    if (open) {
      setIframeStatus('loading');
    }
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);
  
  // Handle iframe load event
  const handleIframeLoad = useCallback(() => {
    // Clear the timeout since we got a response
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Try to access iframe content to verify it loaded successfully
    // This will throw an error if blocked by X-Frame-Options/CSP
    try {
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow) {
        // Try to access location - this throws if blocked
        // We can't actually read the location due to CORS, but if the iframe
        // loaded successfully, we'll get here without error
        setIframeStatus('success');
      }
    } catch (e) {
      // Access denied - likely blocked by X-Frame-Options or CSP
      console.log('Iframe blocked by security policy, showing fallback');
      setIframeStatus('failed');
    }
  }, []);
  
  // Handle iframe error event
  const handleIframeError = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIframeStatus('failed');
  }, []);
  
  // Set up timeout for iframe loading
  useEffect(() => {
    if (isDialogOpen && hasValidExternalUrl && iframeStatus === 'loading') {
      // Set a timeout - if iframe doesn't load within 5 seconds, show fallback
      timeoutRef.current = setTimeout(() => {
        console.log('Iframe load timeout, showing fallback');
        setIframeStatus('failed');
      }, 5000);
    }
    
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isDialogOpen, iframeStatus]);

  // Parse context for NTB data if it's available
  const ntbData = React.useMemo(() => {
    if (!context || typeof context !== 'string') return null;
    
    try {
      const parsed = JSON.parse(context);
      // Check if it's NTB data (has id and ntbId or title)
      if (parsed && (parsed.id || parsed.ntbId || parsed.title)) {
        return parsed;
      }
    } catch (e) {
      // Not JSON, ignore
    }
    return null;
  }, [context]);

  // Get image data - prioritize contextImageUrl/Caption, fallback to parsed context
  const imageData = React.useMemo(() => {
    // First, check if we have direct context image data
    if (contextImageUrl) {
      return {
        imageUrl: contextImageUrl,
        imageCaption: contextImageCaption || ''
      };
    }

    // Fallback: try to extract from ntbData/context
    if (ntbData) {
      const directUrl = ntbData.imageUrl || '';
      const directCaption = ntbData.imageCaption || '';
      if (directUrl) {
        return { imageUrl: directUrl, imageCaption: directCaption };
      }

      // Fallback to mediaList if available
      const mediaList = ntbData.mediaList || ntbData.media || ntbData.media_list;
      if (Array.isArray(mediaList) && mediaList.length) {
        const preferred = mediaList.find((m: any) => 
          /(image|photo|picture)/i.test(m?.type || m?.contentType || m?.mediaType || '')
        ) || mediaList[0];
        const url = preferred?.url || preferred?.href || preferred?.mediaUrl || preferred?.imageUrl || '';
        const caption = preferred?.caption || preferred?.description || preferred?.credit || ntbData?.title || '';
        if (url) {
          return { imageUrl: url, imageCaption: caption };
        }
      }
    }

    return null;
  }, [contextImageUrl, contextImageCaption, ntbData]);

  // --- Hjelpefunksjoner ---
  const convertToMarkdown = (text: string) => text.replace(/^•\s*/gm, '- ');
  const isNTBArticle = (url?: string) => url?.startsWith('ntb:');
  
  // Check if we have a valid external URL (not NTB)
  const hasValidExternalUrl = contextUrl && 
    contextUrl.trim().length > 0 && 
    !isNTBArticle(contextUrl) &&
    (contextUrl.startsWith('http://') || contextUrl.startsWith('https://'));

  const getNTBTitle = (context: string) => {
    try {
      const ntbData = JSON.parse(context);
      return ntbData?.title || 'NTB Artikkel';
    } catch {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = context;
      return tempDiv.querySelector('.lead')?.textContent?.trim() || tempDiv.textContent?.slice(0, 100) + '...' || 'NTB Artikkel';
    }
  };

  // Get title for external links from context
  const getExternalTitle = () => {
    if (!context) return 'Les artikkel';
    
    // Try to parse as JSON (new format with external_article)
    try {
      const data = JSON.parse(context);
      if (data.type === 'external_article' && data.title) {
        return data.title;
      }
    } catch {
      // Not JSON, continue with legacy logic
    }
    
    // Legacy: if context is just a URL or starts with "Ekstern artikkel:", use generic title
    if (context.startsWith('http') || context.startsWith('Ekstern artikkel:') || context === 'Manuell kontekst') {
      return 'Les artikkel';
    }
    
    // Otherwise use the context as title (truncated if needed)
    return context.length > 100 ? context.substring(0, 100) + '...' : context;
  };

  // --- RENDER-FUNKSJONER ---
  const renderContext = () => {
    if (!context) return null;
    
    // Don't show NTB/external headers for Briefr Bullets articles
    if (isBriefBulletsArticle) return null;
    
    // Handle NTB articles
    if (isNTBArticle(contextUrl)) {
      return (
        <div className="mb-4">
          <span 
            className="text-base font-medium text-foreground cursor-pointer hover:text-primary transition-colors inline"
            onClick={() => setIsDialogOpen(true)}
          >
            {getNTBTitle(context)} <ExternalLink className="h-4 w-4 inline ml-1" />
          </span>
        </div>
      );
    }
    
    // Handle external URLs with same styling as NTB
    if (hasValidExternalUrl) {
      return (
        <div className="mb-4">
          <span 
            className="text-base font-medium text-foreground cursor-pointer hover:text-primary transition-colors inline"
            onClick={() => setIsDialogOpen(true)}
          >
            {getExternalTitle()} <ExternalLink className="h-4 w-4 inline ml-1" />
          </span>
        </div>
      );
    }
    
    return null;
  };

  const NTBArticleDialog = () => {
    if (!isNTBArticle(contextUrl)) return null;

    let ntbData;
    try {
      // VIKTIG: Parser 'context'-strengen til et JSON-objekt
      ntbData = JSON.parse(context);
    } catch (e) {
      // Hvis parsing feiler, er det sannsynligvis ren HTML (legacy-format)
      return (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="w-full max-w-4xl h-[80vh]">
            <div className="w-full h-full overflow-auto p-6 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: context }} />
          </DialogContent>
        </Dialog>
      );
    }

    // Hvis parsing er vellykket, render den nye, robuste komponenten
    // Debug: Log available date fields
    React.useEffect(() => {
      if (ntbData) {
        console.log('NTB Data fields:', Object.keys(ntbData));
        console.log('Available date fields:', {
          publishedDate: ntbData.publishedDate,
          published: ntbData.published,
          date: ntbData.date,
          timestamp: ntbData.timestamp
        });
      }
    }, [ntbData]);

    return (
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="w-full max-w-4xl h-[80vh]">
          <div className="w-full h-full overflow-auto p-6 space-y-4">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">{ntbData.title}</DialogTitle>
            </DialogHeader>
            
            {/* Display publication date and time from NTB API */}
            {ntbData.publishedDate && (
              <p className="text-sm text-muted-foreground">
                Publisert: {new Date(ntbData.publishedDate).toLocaleDateString('nb-NO', {
                  day: '2-digit',
                  month: '2-digit', 
                  year: 'numeric'
                })}, kl. {new Date(ntbData.publishedDate).toLocaleTimeString('nb-NO', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            )}
            
            {/* BRUK DE SEPARATE BILDE-FELTENE FØRST, FALL TILBAKE PÅ IMAGEDATA */}
            {imageData && (
              <div className="mb-4">
                <figure className="my-6">
                  <img 
                    src={imageData.imageUrl} 
                    alt={imageData.imageCaption || ntbData.title}
                    className="w-full h-auto rounded-md mx-auto block max-w-lg"
                  />
                  {imageData.imageCaption && (
                    <figcaption className="text-sm text-muted-foreground italic mt-2 text-center">
                      {imageData.imageCaption}
                    </figcaption>
                  )}
                </figure>
              </div>
            )}

            <div 
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ 
                __html: (ntbData.content || '').replace(/<img[^>]*>/g, '').replace(/<figure[^>]*>.*?<\/figure>/gs, '') 
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div className={cn(
      "flex flex-col md:flex-row gap-4 md:gap-6 items-center",
      isReversed ? "md:flex-row-reverse" : ""
    )}>
      <div className="flex justify-center overflow-visible">
        <InterviewMedia 
          intervieweeName={intervieweeName} 
          photoUrl={photoUrl}
          photoUrl2={photoUrl2}
          sponsorLogoUrl={(sponsorLogoPosition === 'profile' || sponsorLogoPosition === 'both') ? sponsorLogoUrl : undefined}
          interviewId={interviewId}
        />
      </div>
      
      <div className="w-full md:w-3/4">  {/* Økt fra md:w-1/2 til md:w-3/4 */}
        {renderContext()}
        
        <Link 
          to={`/question/${encodeURIComponent(question)}`}
          onClick={() => attention?.trackClick('question')}
        >
          <h2 className="text-lg font-semibold cursor-pointer hover:text-primary transition-colors" style={{ color: '#ea384c' }}>{question}</h2>
        </Link>
        
        <div className="mt-2 mb-3">
          <span 
            className="font-medium text-foreground block cursor-pointer transition-colors hover:text-primary"
            onClick={() => {
              attention?.trackClick('person');
              window.location.href = `/person/${encodeURIComponent(intervieweeName)}`;
            }}
          >
            {intervieweeName}
          </span>
          {(jobTitle || company) && (
            <div className="text-sm text-muted-foreground">
              {company ? (
                <Link 
                  to={`/company/${encodeURIComponent(company)}`}
                  className="cursor-pointer transition-colors hover:text-primary"
                  onClick={() => attention?.trackClick('company')}
                >
                  {jobTitle ? `${jobTitle} · ${company}` : company}
                </Link>
              ) : jobTitle ? (
                <span>{jobTitle}</span>
              ) : null}
            </div>
          )}
        </div>
        
        <div className={cn(
          "my-4 relative",
          !isExpanded && "overflow-hidden after:absolute after:bottom-0 after:left-0 after:right-0 after:h-12 after:bg-gradient-to-t after:from-white after:to-transparent after:pointer-events-none"
        )}
        style={{ maxHeight: !isExpanded ? '52px' : 'none' }}
        >
          <div className="">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({node, ...props}) => (
                  <h1 className="!text-base !my-3 !font-medium" style={{ fontWeight: 500 }} {...props} />
                ),
                h2: ({node, ...props}) => (
                  <h2 className="!text-base !my-2 !font-medium" style={{ fontWeight: 500 }} {...props} />
                ),
                h3: ({node, ...props}) => (
                  <h3 className="!text-base !my-2 !font-medium" style={{ fontWeight: 500 }} {...props} />
                ),
                p: ({node, ...props}) => (
                  <p className="mb-3 leading-relaxed" {...props} />
                ),
                ul: ({node, ...props}) => (
                  <ul className="list-disc list-inside pl-4 my-2" {...props} />
                ),
                ol: ({node, ...props}) => (
                  <ol className="list-decimal list-inside pl-4 my-2" {...props} />
                )
              }}
            >
              {preprocessMarkdown(answer)}
            </ReactMarkdown>
          </div>
        </div>
        
        {/* Hashtags after answer */}
        {tags && tags.length > 0 && (
          <div className="mt-3 mb-3">
            {tags
              .filter(tag => typeof tag === 'string' && tag.trim() !== '' && tag !== '{}' && tag !== '"#{}"')
              .map((tag, index) => {
                // Clean the tag thoroughly - enhanced cleaning
                let cleanTag = String(tag)
                  .replace(/^["']|["']$/g, '') // Remove surrounding quotes
                  .replace(/["']/g, '') // Remove ANY quotes within the string
                  .replace(/^#+/, '') // Remove any leading hashtags
                  .replace(/[{}]/g, '') // Remove curly braces
                  .replace(/\s+/g, ' ') // Replace multiple spaces with single space
                  .replace(/[^\w\sæøåÆØÅ-]/g, '') // Remove special characters except Norwegian letters and hyphens
                  .trim();
                
                // Skip empty tags after cleaning
                if (!cleanTag) return null;
                
                return (
                  <Badge key={`${cleanTag}-${index}`} className="mr-2 bg-muted text-muted-foreground hover:bg-muted/80 border-transparent">
                    <Link 
                      to={`/tag/${encodeURIComponent(cleanTag)}`}
                      onClick={() => attention?.trackClick('tag')}
                    >
                      #{cleanTag}
                    </Link>
                  </Badge>
                );
              })
              .filter(Boolean)} {/* Remove null entries */}
          </div>
        )}
        
        {/* Photographer credit and sponsor logo in article position - only show when expanded */}
        {isExpanded && (photographerCredit || (isSponsored && sponsorLogoUrl && (sponsorLogoPosition === 'article' || sponsorLogoPosition === 'both'))) && (
          <div className="flex items-center gap-3 mt-2">
            {photographerCredit && (
              <p className="text-xs text-muted-foreground italic">
                Foto: {photographerCredit}
              </p>
            )}
            {isSponsored && sponsorLogoUrl && (sponsorLogoPosition === 'article' || sponsorLogoPosition === 'both') && (
              <div className="rounded-full overflow-hidden bg-white shadow-lg flex-shrink-0" style={{ width: '144px', height: '144px' }}>
                <img 
                  src={sponsorLogoUrl} 
                  alt="Sponsor logo"
                  className="w-full h-full object-contain p-4"
                  onError={(e) => {
                    (e.target as HTMLImageElement).parentElement!.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
        )}
        
        {/* Article sources - only show when expanded */}
        {isExpanded && <ArticleSources sources={sourceArticles} tags={tags} />}
      </div>
      
      <NTBArticleDialog />
      
      {/* Dialog for external URLs with smart iframe loading */}
      {hasValidExternalUrl && (
        <Dialog open={isDialogOpen} onOpenChange={handleDialogOpen}>
          <DialogContent className={cn(
            "w-full transition-all duration-300",
            iframeStatus === 'success' ? "max-w-4xl h-[85vh]" : "max-w-md"
          )}>
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">
                {getExternalTitle()}
              </DialogTitle>
            </DialogHeader>
            
            {/* Loading state */}
            {iframeStatus === 'loading' && (
              <div className="py-8 flex flex-col items-center justify-center gap-4">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <p className="text-muted-foreground text-sm">Laster inn artikkel...</p>
              </div>
            )}
            
            {/* Iframe - hidden when failed, visible when loading (to trigger load event) or success */}
            <iframe
              ref={iframeRef}
              src={isDialogOpen ? contextUrl : undefined}
              className={cn(
                "w-full border-0 rounded-md",
                iframeStatus === 'success' ? "h-[calc(85vh-100px)]" : "h-0 opacity-0 absolute"
              )}
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
              title={getExternalTitle()}
            />
            
            {/* Fallback state - shown when iframe fails to load */}
            {iframeStatus === 'failed' && (
              <div className="py-4">
                <p className="text-muted-foreground mb-4">
                  Denne artikkelen kan ikke vises direkte her. Klikk på knappen under for å lese den i en ny fane.
                </p>
                <a 
                  href={contextUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                  onClick={() => handleDialogOpen(false)}
                >
                  <ExternalLink className="h-4 w-4" />
                  Åpne artikkelen i ny fane
                </a>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ContentLayout;